import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import React from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';

import { StepContextProvider, useStepContext } from '@post-office/message-step-context';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { PlacementFrontend } from '@post-office/browser--cc-ai/audio-player-spotlight-tour';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';

import { type MessageTemplateContext } from '../../../index';

import { registeredContent } from '../../in-app/screen-space-flags/messages/i18n';

import { useGasv3Listener } from '@atlassian/post-office-context/analytics-listener/hook';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import { type Placement } from '@atlaskit/spotlight/types';

type PlacementRenderProps = {
	onDismiss: () => void;
	context?: MessageTemplateContext;
};

type StepData = {
	headline: MessageDescriptor;
	bodyText: MessageDescriptor;
	placement: Placement;
	targetSelector: string;
};

const messages = registeredContent.default;

const steps: StepData[] = [
	{
		headline: messages.step1Headline,
		bodyText: messages.step1Body,
		placement: 'right-end',
		targetSelector: '#audio-player-changeboarding-step-1',
	},
	{
		headline: messages.step2Headline,
		bodyText: messages.step2Body,
		placement: 'bottom-end',
		targetSelector: '#audio-player-changeboarding-step-2',
	},
	{
		headline: messages.step3Headline,
		bodyText: messages.step3Body,
		placement: 'left-start',
		targetSelector: '#audio-player-changeboarding-step-3',
	},
];

const AudioPlayerStepProvider = ({ onDismiss }: { onDismiss: () => void }) => {
	return (
		<StepContextProvider steps={[...steps]}>
			<AudioPlayerStepController onDismiss={onDismiss} />
		</StepContextProvider>
	);
};

// Step Controller Component - mounts spotlight to specific DOM elements
const AudioPlayerStepController = ({ onDismiss }: { onDismiss: () => void }) => {
	const { currentStep } = useStepContext();
	const currentStepData = steps[currentStep - 1];

	const renderInline = useMountToHtmlElement({
		selectors: currentStepData?.targetSelector ?? '',
		as: 'inline',
		position: 'inside',
	});

	if (!currentStepData) return null;

	return <>{renderInline(<AudioPlayerSpotlight {...currentStepData} onDismiss={onDismiss} />)}</>;
};

export const AudioPlayerSpotlight = ({
	headline,
	bodyText,
	placement,
	onDismiss,
}: StepData & {
	onDismiss: () => void;
}): React.JSX.Element => {
	const { next, previous, currentStep, totalSteps } = useStepContext();
	const { onAcknowledged, onStepChanged, onClosed } = usePostOfficeUserInteractions();
	const { formatMessage } = useIntl();

	const isFirstStep = currentStep === 1;
	const isLastStep = currentStep === totalSteps;

	const handleNext = (): void => {
		if (isLastStep) {
			void onAcknowledged({ interactionIdentifier: 'done' });
			onDismiss();
		} else {
			void onStepChanged({
				interactionIdentifier: 'next',
				fromStep: currentStep,
				toStep: currentStep + 1,
				direction: 'next',
			});
			next();
		}
	};

	const handlePrevious = (): void => {
		if (!isFirstStep) {
			void onStepChanged({
				interactionIdentifier: 'back',
				fromStep: currentStep,
				toStep: currentStep - 1,
				direction: 'previous',
			});
			previous();
		}
	};

	const handleDismiss = (): void => {
		void onClosed({ interactionIdentifier: 'dismiss' });
		onDismiss();
	};

	return (
		<PopoverProvider>
			<PopoverTarget>
				{/* Dummy target */}
				<div />
			</PopoverTarget>
			<PopoverContent
				dismiss={handleDismiss}
				placement={placement}
				shouldDismissOnClickOutside={false}
			>
				<SpotlightCard testId="spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(headline)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleDismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>{formatMessage(bodyText)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>
							{formatMessage(messages.stepIndicator, { currentStep, totalSteps })}
						</SpotlightStepCount>
						<SpotlightActions>
							{!isFirstStep && (
								<SpotlightSecondaryAction onClick={handlePrevious}>
									{formatMessage(messages.back)}
								</SpotlightSecondaryAction>
							)}
							<SpotlightPrimaryAction onClick={handleNext}>
								{isLastStep ? formatMessage(messages.done) : formatMessage(messages.next)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};

const eventToMatch = {
	eventType: 'screen',
	name: 'audioContainer',
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({ onDismiss }: PlacementRenderProps) => {
			const showMessage = useGasv3Listener(eventToMatch);

			return showMessage ? <AudioPlayerStepProvider onDismiss={onDismiss} /> : null;
		},
	},
});
